// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Mantine Theme
// Aligned with OpenGov Capital Design System tokens
// ═══════════════════════════════════════════════════════════════

import { createTheme } from '@mantine/core';

export const theme = createTheme({
  fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: '"DM Mono", "Fira Code", "JetBrains Mono", monospace',

  primaryColor: 'blurple',
  colors: {
    // GAB Design System: primary (blurple)
    blurple: [
      '#F7F8FD', // 50
      '#EEF1FC', // 100
      '#D4DDFB', // 200
      '#B8C6FB', // 300
      '#94A8FF', // 400
      '#7589FF', // 500
      '#5A65FF', // 600 — primary main
      '#4B3FFF', // 700 — brand blurple
      '#2C01D6', // 800 — navy
      '#19009B', // 900 — dark navy
    ],
    // GAB Design System: secondary (slate)
    slate: [
      '#F7F9FA', // 50
      '#F0F2F4', // 100
      '#D8DFE5', // 200
      '#BECBD6', // 300
      '#9CB1C3', // 400
      '#8099AE', // 500
      '#758CA0', // 600 — secondary main
      '#546574', // 700
      '#3F4C58', // 800
      '#2B343D', // 900
    ],
    // GAB Design System: neutral (gray)
    neutral: [
      '#F8F8F8', // 50
      '#F2F2F2', // 100
      '#DDDEDE', // 200
      '#C8C9CA', // 300
      '#ADAFB1', // 400
      '#939598', // 500
      '#7B7D7F', // 600
      '#616365', // 700
      '#494A4C', // 800
      '#323334', // 900
    ],
    // GAB Design System: red (error)
    red: [
      '#FCF7F7', // light
      '#F9E5E4',
      '#F0C2C0',
      '#E59491',
      '#D66562',
      '#D33423', // main — GAB error color
      '#B12525',
      '#8F1E1E',
      '#610F11', // dark
      '#3A0808',
    ],
    // GAB Design System: green (success)
    green: [
      '#EFFDF1', // light
      '#D4F5DA',
      '#A8E8B8',
      '#6DD68E',
      '#3DC06A',
      '#1FCA6C', // accent
      '#037730', // main — GAB success color
      '#026228',
      '#003E20', // dark
      '#002314',
    ],
    // GAB Design System: yellow (warning)
    yellow: [
      '#FDF8F1', // light
      '#F8ECDA',
      '#EDD4AB',
      '#DEBA7A',
      '#CCA04E',
      '#B8862E',
      '#885604', // main — GAB warning color
      '#6E4503',
      '#472D01', // dark
      '#2A1B00',
    ],
    // GAB Design System: cyan (info)
    cyan: [
      '#F1FAFC',
      '#D4EFF5',
      '#A4DDE9',
      '#6DC8D9',
      '#3DB0C7',
      '#1A97B3',
      '#0E6F7F', // main — GAB info color
      '#0A5A67',
      '#043A44', // dark
      '#022229',
    ],
  },

  // GAB Design System: border radius
  radius: {
    xs: '0.25rem',   // GAB --radius-sm (4px)
    sm: '0.375rem',  // GAB --radius-md (6px)
    md: '0.5rem',    // GAB --radius-lg (8px)
    lg: '0.75rem',   // GAB --radius-xl (12px)
    xl: '1rem',      // GAB --radius-2xl (16px)
  },

  // GAB Design System: layered shadows
  shadows: {
    xs: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // GAB Design System: typography
  headings: {
    fontWeight: '700',  // GAB --font-weight-bold for headings
    sizes: {
      h1: { fontSize: '2.25rem', lineHeight: '1.25' },  // GAB --font-size-4xl
      h2: { fontSize: '1.875rem', lineHeight: '1.25' }, // GAB --font-size-3xl
      h3: { fontSize: '1.5rem', lineHeight: '1.25' },   // GAB --font-size-2xl
      h4: { fontSize: '1.25rem', lineHeight: '1.25' },  // GAB --font-size-xl
      h5: { fontSize: '1.125rem', lineHeight: '1.25' }, // GAB --font-size-lg
      h6: { fontSize: '1rem', lineHeight: '1.5' },      // GAB --font-size-base
    },
  },

  // GAB Design System: spacing (rem-based, mapped to closest GAB tokens)
  spacing: {
    xs: '0.25rem',  // GAB --spacing-1 (4px)
    sm: '0.5rem',   // GAB --spacing-2 (8px)
    md: '1rem',     // GAB --spacing-4 (16px)
    lg: '1.5rem',   // GAB --spacing-6 (24px)
    xl: '2rem',     // GAB --spacing-8 (32px)
  },

  defaultRadius: 'xs',   // GAB --radius-sm (0.25rem / 4px)

  components: {
    Button: { defaultProps: { size: 'sm' } },
    TextInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    Textarea: { defaultProps: { size: 'sm' } },
    Modal: { defaultProps: { centered: true } },
    Paper: {
      defaultProps: { shadow: 'none', withBorder: true },
    },
  },
});
